export interface CollectionRosterRow {
  id: string
  name: string
  detail: string
  state: string
  unlocked: boolean
  active: boolean
  affordable: boolean
}

export interface CollectionOwnedRow {
  id: string
  rarity: string
  name: string
  count: number
  color: string
  isChar: boolean
  equipped: boolean
  rightLabel: string
}

export interface CollectionOverlayOptions {
  title: string
  rosterTitle: string
  tapEquip: string
  tapEquipShort: string
  loadingText: string
  backLabel: string
  onBack: () => void
  onRosterTap: (id: string) => void
  onOwnedTap: (id: string) => void
}

export interface CollectionOverlayHandle {
  setRoster: (soulsLine: string, rows: CollectionRosterRow[]) => void
  setOwned: (status: string, rows: CollectionOwnedRow[]) => void
  setNote: (text: string, tone?: 'bad' | 'good' | 'sub') => void
  destroy: () => void
}

export function mountCollectionOverlay(options: CollectionOverlayOptions): CollectionOverlayHandle {
  const root = document.createElement('div')
  root.className = 'game-collection-overlay'

  const panel = document.createElement('section')
  panel.className = 'game-collection-panel'

  const title = document.createElement('h1')
  title.className = 'game-collection-title'
  title.textContent = options.title

  const note = document.createElement('p')
  note.className = 'game-collection-note'

  const roster = document.createElement('section')
  roster.className = 'game-collection-card'
  const rosterHead = document.createElement('div')
  rosterHead.className = 'game-collection-section-head'
  const rosterTitle = document.createElement('h2')
  rosterTitle.textContent = options.rosterTitle
  const souls = document.createElement('span')
  rosterHead.append(rosterTitle, souls)
  const rosterList = document.createElement('div')
  rosterList.className = 'game-collection-list'
  roster.append(rosterHead, rosterList)

  const owned = document.createElement('section')
  owned.className = 'game-collection-card'
  const ownedStatus = document.createElement('p')
  ownedStatus.className = 'game-collection-owned-status'
  ownedStatus.textContent = options.loadingText
  const ownedList = document.createElement('div')
  ownedList.className = 'game-collection-list'
  owned.append(ownedStatus, ownedList)

  const back = document.createElement('button')
  back.type = 'button'
  back.className = 'game-collection-btn'
  back.textContent = options.backLabel
  back.addEventListener('click', options.onBack)

  panel.append(title, note, roster, owned, back)
  root.append(panel)
  document.body.append(root)

  return {
    setRoster: (soulsLine, rows) => {
      souls.textContent = soulsLine
      rosterList.innerHTML = ''
      for (const row of rows) {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = [
          'game-collection-row',
          row.active ? 'is-active' : '',
          row.unlocked ? 'is-unlocked' : 'is-locked',
          row.affordable ? 'is-affordable' : '',
        ].filter(Boolean).join(' ')
        btn.addEventListener('click', () => options.onRosterTap(row.id))
        btn.innerHTML = `<span><strong>${escapeHtml(row.name)}</strong><em>${escapeHtml(row.detail)}</em></span><b>${escapeHtml(row.state)}</b>`
        rosterList.append(btn)
      }
    },
    setOwned: (status, rows) => {
      ownedStatus.textContent = status
      ownedList.innerHTML = ''
      if (rows.length > 0) ownedStatus.textContent = options.tapEquip
      for (const row of rows) {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = `game-collection-row owned ${row.equipped ? 'is-active' : ''}`
        btn.style.setProperty('--rarity', row.color)
        btn.disabled = !row.isChar
        btn.addEventListener('click', () => options.onOwnedTap(row.id))
        btn.innerHTML = `<span><strong>${escapeHtml(row.rarity)} · ${escapeHtml(row.name)}</strong><em>${escapeHtml(row.isChar ? options.tapEquipShort : '')}</em></span><b>${escapeHtml(row.rightLabel)}</b>`
        ownedList.append(btn)
      }
    },
    setNote: (text, tone = 'bad') => {
      note.textContent = text
      note.hidden = !text
      note.dataset.tone = tone
    },
    destroy: () => root.remove(),
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
